import useBlockedUsers from '@contexts/useBlockedUsers';
import {
  QueryKeys,
  assertParameterIsNotUndefinedOrNull,
  followsApi,
  useAuthMutation,
  useAuthQuery,
} from '@sunrise-ui/api-client';
import {
  FollowableEntityType,
  type FollowableEntity,
  type FollowsInput,
} from '@sunrise-ui/api/network';
import { useAuthStore, useSnackbar } from '@sunrise-ui/primitives';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useMemo } from 'react';

const { followingKey, followersKey } = QueryKeys.follows;
const { relationshipStatsKey } = QueryKeys.people;

type Props = {
  silenceErrors?: boolean;
};

export const useFollowUser = ({ silenceErrors }: Props = {}) => {
  const invalidateFollowsAndStats = useInvalidateFollowsAndStats();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<FollowableEntity, unknown, string>({
    async mutationFn(userId: string) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const followsInput: FollowsInput = {
        follower_id: currentUserId,
        followed_id: userId,
        followed_entity_type: FollowableEntityType.Person,
      };
      const response = await followsApi.followEntityFollowsPost({
        followsInput,
      });
      return response.data;
    },
    async onSuccess(_, userId) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      await invalidateFollowsAndStats(currentUserId, userId);
    },
    async onError(error) {
      if (!silenceErrors) {
        if (isAxiosError(error) && error.response?.status === 400) {
          showSnackbar({
            type: 'error',
            text: "You're already following this user!",
          });
        } else {
          showSnackbar({
            type: 'error',
            text: 'Failed to follow user. Please try again',
          });
        }
      }
    },
  });
};

export const useUnfollowUser = () => {
  const invalidateFollowsAndStats = useInvalidateFollowsAndStats();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<FollowableEntity, unknown, string>({
    async mutationFn(userId: string) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const followsInput: FollowsInput = {
        follower_id: currentUserId,
        followed_id: userId,
        followed_entity_type: FollowableEntityType.Person,
      };
      const response = await followsApi.unfollowEntityFollowsUnfollowPost({
        followsInput,
      });
      return response.data;
    },
    async onSuccess(_, userId) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      await invalidateFollowsAndStats(currentUserId, userId);
    },
    async onError() {
      showSnackbar({
        text: 'Failed to unfollow user. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useFollowCommunity = ({ silenceErrors }: Props = {}) => {
  const invalidateFollowsAndStats = useInvalidateFollowsAndStats();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<FollowableEntity, unknown, string>({
    async mutationFn(communityId: string) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const followsInput: FollowsInput = {
        follower_id: currentUserId,
        followed_id: communityId,
        followed_entity_type: FollowableEntityType.Community,
      };
      const response = await followsApi.followEntityFollowsPost({
        followsInput,
      });
      return response.data;
    },
    async onSuccess(_, communityId) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      await invalidateFollowsAndStats(currentUserId, communityId);
    },
    async onError() {
      if (!silenceErrors) {
        showSnackbar({
          text: 'Failed to follow community. Please try again.',
          type: 'error',
        });
      }
    },
  });
};

export const useUnfollowCommunity = () => {
  const invalidateFollowsAndStats = useInvalidateFollowsAndStats();
  const { showSnackbar } = useSnackbar();

  return useAuthMutation<FollowableEntity, unknown, string>({
    async mutationFn(communityId: string) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      const followsInput: FollowsInput = {
        follower_id: currentUserId,
        followed_id: communityId,
        followed_entity_type: FollowableEntityType.Community,
      };
      const response = await followsApi.unfollowEntityFollowsUnfollowPost({
        followsInput,
      });
      return response.data;
    },
    async onSuccess(_, communityId) {
      const { currentUserId } = useAuthStore.getState();
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      await invalidateFollowsAndStats(currentUserId, communityId);
    },
    async onError() {
      showSnackbar({
        text: 'Failed to unfollow community. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useGetFollowing = () => {
  const { currentUserId } = useAuthStore();
  const { isLoading: isLoadingBlockedUsers, isUserBlocked } = useBlockedUsers();

  const {
    isLoading: isLoadingFollowing,
    data,
    ...otherProps
  } = useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      return followsApi
        .getFollowingFollowsUserIdFollowingGet({
          userId: currentUserId,
        })
        .then((response) => response.data);
    },
    queryKey: [followingKey, currentUserId],
    enabled: !isLoadingBlockedUsers,
  });

  const filteredData: FollowableEntity[] | undefined = useMemo(
    () =>
      (data as FollowableEntity[] | undefined)
        ?.filter((entity) => entity.entity_type === FollowableEntityType.Person)
        .filter((entity) => !isUserBlocked(entity.entity_id)),
    [data, isUserBlocked],
  );

  return {
    isLoading: isLoadingFollowing || isLoadingBlockedUsers,
    data: filteredData,
    ...otherProps,
  };
};

export const useGetFollowingCommunities = () => {
  const { currentUserId } = useAuthStore();

  const {
    isLoading: isLoadingFollowing,
    data,
    ...otherProps
  } = useAuthQuery({
    queryFn: async () => {
      assertParameterIsNotUndefinedOrNull(currentUserId, 'currentUserId');
      return followsApi
        .getFollowingFollowsUserIdFollowingGet({
          userId: currentUserId,
        })
        .then((response) => response.data);
    },
    queryKey: [followingKey, currentUserId],
  });

  const filteredData: FollowableEntity[] | undefined = useMemo(
    () =>
      (data as FollowableEntity[] | undefined)?.filter(
        (entity) => entity.entity_type === FollowableEntityType.Community,
      ),
    [data],
  );

  return {
    isLoading: isLoadingFollowing,
    data: filteredData,
    ...otherProps,
  };
};

export const useIsFollowingCommunity = (communityId?: string) => {
  const { data: followingCommunities, isLoading } =
    useGetFollowingCommunities();

  const isFollowing = useMemo(() => {
    if (!communityId || !followingCommunities) return false;
    return followingCommunities.some(
      (entity) => entity.entity_id === communityId,
    );
  }, [communityId, followingCommunities]);

  return {
    isFollowing,
    isLoading,
  };
};

export const useGetFollowers = ({ userId }: { userId: string }) => {
  const { isLoading: isLoadingBlockedUsers, isUserBlocked } = useBlockedUsers();

  const {
    isLoading: isLoadingFollowers,
    data,
    ...otherProps
  } = useAuthQuery({
    async queryFn() {
      return followsApi
        .getFollowersFollowsUserIdFollowersGet({
          userId,
        })
        .then((response) => response.data);
    },
    queryKey: [followersKey, userId],
    enabled: Boolean(userId) && !isLoadingBlockedUsers,
  });

  const filteredData = useMemo(
    () => data?.filter((user) => !isUserBlocked(user.user_id)),
    [data, isUserBlocked],
  );

  return {
    isLoading: isLoadingFollowers || isLoadingBlockedUsers,
    data: filteredData,
    ...otherProps,
  };
};

const useInvalidateFollowsAndStats = () => {
  const queryClient = useQueryClient();
  return async (currentUserId: string, otherUserId: string) => {
    await queryClient.invalidateQueries({
      queryKey: [followingKey, currentUserId],
    });
    await queryClient.invalidateQueries({
      queryKey: [followersKey, otherUserId],
    });
    await queryClient.invalidateQueries({
      queryKey: [relationshipStatsKey, currentUserId],
    });
    await queryClient.invalidateQueries({
      queryKey: [relationshipStatsKey, otherUserId],
    });
  };
};
